<template>
  <div class="portal-page auth-page find-id-page">
    <div class="page-shell auth-page__shell">
      <header class="auth-page__head">
        <p class="auth-page__eyebrow">계정</p>
        <h1 class="auth-page__title">아이디 찾기</h1>
        <p class="auth-page__desc">
          가입 시 등록한 정보와 본인 인증으로 아이디를 확인할 수 있습니다.
        </p>
      </header>

      <form class="auth-page__panel auth-form find-id-form" @submit.prevent>
        <div class="auth-form__body">
          <label class="auth-form__field">
            <span>이름</span>
            <input
              v-model="form.name"
              type="text"
              autocomplete="name"
              placeholder="이름을 입력해 주세요"
              maxlength="20"
              :class="nameInputState"
              @input="onNameInput"
              @blur="nameTouched = true"
            />
            <p v-if="showNameWarning" class="auth-form__msg auth-form__msg--warn">
              한글 2자 이상 입력해 주세요.
            </p>
          </label>

          <div class="auth-form__field">
            <span>인증 방법</span>
            <select v-model="verifyMethod" class="find-id-form__select">
              <option value="email">이메일 인증</option>
              <option value="phone">휴대폰 인증</option>
            </select>
          </div>

          <div v-if="verifyMethod === 'email'" class="auth-form__field">
            <span>이메일</span>
            <div class="auth-form__field-row">
              <input
                v-model="form.email"
                type="email"
                autocomplete="email"
                placeholder="example@email.com"
                :class="emailInputState"
                :disabled="isEmailInputLocked"
              />
              <button
                type="button"
                class="auth-form__inline-btn"
                :class="{ 'auth-form__inline-btn--done': verification.codeSent }"
                :disabled="!isEmailValid(form.email) || verification.verified || verification.sending || verification.resendIn > 0"
                @click="sendCode"
              >
                {{ verification.sendActionLabel('인증발송', '재발송') }}
              </button>
            </div>
            <p v-if="verifyMethod === 'email' && verification.errorMessage && !verification.codeSent" class="auth-form__msg auth-form__msg--warn">
              {{ verification.errorMessage }}
            </p>
            <p v-else-if="verifyMethod === 'email' && verification.codeSent && !verification.verified" class="auth-form__msg auth-form__msg--ok">
              {{ form.email }}으로 인증번호를 보냈습니다. 받은편지함과 스팸함을 확인해 주세요.
            </p>
          </div>

          <div v-else class="auth-form__field">
            <span>휴대폰 번호</span>
            <div class="auth-form__field-row">
              <input
                v-model="form.phone"
                type="tel"
                inputmode="numeric"
                autocomplete="tel"
                placeholder="01012345678"
                maxlength="11"
                :class="phoneInputState"
                :disabled="isPhoneInputLocked"
                @input="onPhoneInput"
              />
              <button
                type="button"
                class="auth-form__inline-btn"
                :class="{ 'auth-form__inline-btn--done': verification.codeSent }"
                :disabled="!isPhoneValid(form.phone) || verification.verified || verification.sending || verification.resendIn > 0"
                @click="sendCode"
              >
                {{ verification.sendActionLabel('인증발송', '재발송') }}
              </button>
            </div>
            <p v-if="verifyMethod === 'phone' && verification.errorMessage" class="auth-form__msg auth-form__msg--warn">
              {{ verification.errorMessage }}
            </p>
          </div>

          <div v-if="!verification.verified" class="auth-form__field">
            <span>인증번호</span>
            <div class="auth-form__field-row">
              <input
                v-model="form.verifyCode"
                type="text"
                inputmode="numeric"
                maxlength="6"
                placeholder="인증번호 6자리"
                :class="verifyCodeInputState"
                :disabled="!verification.codeSent"
                @input="onVerifyCodeInput"
              />
              <button
                type="button"
                class="auth-form__inline-btn"
                :disabled="!verification.codeSent || !isVerifyCodeValid(form.verifyCode) || verification.verifying"
                @click="verifyCode"
              >
                {{ verification.verifying ? '확인중...' : '확인' }}
              </button>
            </div>
            <p v-if="!verification.codeSent" class="auth-form__hint">
              인증발송 후 인증번호를 입력해 주세요.
            </p>
            <template v-else>
              <p class="auth-form__msg auth-form__msg--muted">
                인증번호가 발송되었습니다.
                <span v-if="verification.timer > 0" class="auth-form__verify-timer">
                  {{ verification.formatTimer(verification.timer) }}
                </span>
              </p>
              <p v-if="verification.errorMessage" class="auth-form__msg auth-form__msg--warn">
                {{ verification.errorMessage }}
              </p>
            </template>
          </div>
          <p v-if="verification.verified" class="auth-form__msg auth-form__msg--ok">본인 인증이 완료되었습니다.</p>

          <div v-if="found" class="auth-form__result">
            <p class="auth-form__result-label">조회된 아이디</p>
            <p class="auth-form__result-value">jang***</p>
            <p class="auth-form__hint">개인정보 보호를 위해 일부만 표시됩니다.</p>
          </div>
        </div>

        <div class="auth-form__actions">
          <button
            type="button"
            class="btn-primary auth-form__submit"
            :disabled="!verification.verified"
            @click="findId"
          >
            아이디 찾기
          </button>
          <router-link to="/login" class="btn-secondary auth-form__secondary">로그인</router-link>
        </div>

        <div class="auth-form__links">
          <router-link to="/find-password" class="auth-form__link">비밀번호 찾기</router-link>
          <span class="auth-form__divider" aria-hidden="true" />
          <router-link to="/signup" class="auth-form__link">회원가입</router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useVerification } from '@/composables/useVerification'
import {
  inputValidationState,
  deferredInputValidationState,
  shouldShowNameFormatWarning,
  isEmailValid,
  isNameValid,
  isPhoneValid,
  isVerifyCodeValid,
  sanitizeDigitsInput,
  sanitizeNameInput,
} from '@/utils/formValidation'

const form = reactive({
  name: '',
  email: '',
  phone: '',
  verifyCode: '',
})

const verifyMethod = ref<'email' | 'phone'>('email')
const found = ref(false)
const nameTouched = ref(false)
const nameFeedbackAttempted = ref(false)
const verification = useVerification('find-id')

const nameInputState = computed(() =>
  deferredInputValidationState(
    form.name,
    isNameValid(form.name),
    nameTouched.value,
    nameFeedbackAttempted.value,
  ),
)

const showNameWarning = computed(() =>
  shouldShowNameFormatWarning(form.name, nameTouched.value, nameFeedbackAttempted.value),
)

const emailInputState = computed(() =>
  inputValidationState(form.email, {
    isValid: verification.verified,
    isInvalid: Boolean(form.email) && !isEmailValid(form.email),
  }),
)

const phoneInputState = computed(() =>
  inputValidationState(form.phone, {
    isValid: verification.verified,
    isInvalid: Boolean(form.phone) && !isPhoneValid(form.phone),
  }),
)

const verifyCodeInputState = computed(() =>
  inputValidationState(form.verifyCode, isVerifyCodeValid(form.verifyCode)),
)

const isEmailInputLocked = computed(
  () =>
    verification.sending ||
    verification.verified ||
    verification.codeSent,
)

const isPhoneInputLocked = computed(
  () =>
    verification.sending ||
    verification.verified ||
    verification.codeSent,
)

const onNameInput = (event: Event) => {
  form.name = sanitizeNameInput((event.target as HTMLInputElement).value)
}

const onPhoneInput = (event: Event) => {
  form.phone = sanitizeDigitsInput((event.target as HTMLInputElement).value, 11)
}

const onVerifyCodeInput = (event: Event) => {
  form.verifyCode = sanitizeDigitsInput((event.target as HTMLInputElement).value, 6)
}

watch(verifyMethod, () => {
  found.value = false
  form.verifyCode = ''
  verification.reset()
})

watch(
  () => form.email,
  () => {
    if (verifyMethod.value === 'email') {
      form.verifyCode = ''
      verification.reset()
    }
  },
)

watch(
  () => form.phone,
  () => {
    if (verifyMethod.value === 'phone') {
      form.verifyCode = ''
      verification.reset()
    }
  },
)

const sendCode = async () => {
  if (!isNameValid(form.name)) {
    nameFeedbackAttempted.value = true
    return
  }

  found.value = false
  form.verifyCode = ''
  try {
    if (verifyMethod.value === 'email') {
      await verification.sendEmail(form.email)
    } else {
      await verification.sendPhone(form.phone)
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : '인증번호 발송에 실패했습니다.'
    window.alert(message)
  }
}

const verifyCode = async () => {
  try {
    if (verifyMethod.value === 'email') {
      await verification.verifyEmail(form.email, form.verifyCode)
    } else {
      await verification.verifyPhone(form.phone, form.verifyCode)
    }
  } catch {
    // errorMessage는 폼에 표시
  }
}

const findId = () => {
  if (!isNameValid(form.name)) {
    nameFeedbackAttempted.value = true
    return
  }

  if (!verification.verified) return
  found.value = true
}
</script>

<style lang="scss" src="./FindIdPage.scss"></style>
